import { lazy } from 'react';

// project imports
import Loadable from '../components/Loadable';
import DashboardLayout from '../layout/Dashboard';
import AuthGuard from '../utils/route-guard/AuthGuard';
import DevUserGuard from '../utils/route-guard/DevUserGuard';
import PermissionGuard from '../utils/route-guard/PermissionGuard';
import RootGuard from '../utils/route-guard/RootGuard';
import NotFound from '../components/NotFound';

// render - dashboard page
const Dashboard = Loadable(lazy(() => import('../pages/dashboard')));

// render - users page
const Users = Loadable(lazy(() => import('../pages/users')));

// render - clients page
const Clients = Loadable(lazy(() => import('../pages/clients')));

// render - centres page
const Centres = Loadable(lazy(() => import('../pages/centres')));

// render - dentists page
const Dentists = Loadable(lazy(() => import('../pages/dentists')));

// render - brokers page
const Brokers = Loadable(lazy(() => import('../pages/brokers')));

// render - carriers page
const Carriers = Loadable(lazy(() => import('../pages/carriers')));

// render - payments page
const Payments = Loadable(lazy(() => import('../pages/payments')));

// render - claims page (also used for /claims/predeterminations)
const Claims = Loadable(lazy(() => import('../pages/claims')));
const ClaimsPaymentSchedule = Loadable(lazy(() => import('../pages/claims-payment-schedule')));

// render - specialists page
const Specialists = Loadable(lazy(() => import('../pages/specialists')));

// render - emergency dentists page
const EmergencyDentists = Loadable(lazy(() => import('../pages/emergency-dentists')));

// render - divisions page
const Divisions = Loadable(lazy(() => import('../pages/divisions')));

// render - group plans page
const GroupPlans = Loadable(lazy(() => import('../pages/group-plans')));

// render - fee guides page
const FeeGuides = Loadable(lazy(() => import('../pages/fee-guides')));

// render - members page
const Members = Loadable(lazy(() => import('../pages/members')));

// render - dependents page
const Dependents = Loadable(lazy(() => import('../pages/dependents')));

// render - centre transfer (members)
const CentreTransfer = Loadable(lazy(() => import('../pages/centre-transfer')));

// render - expiring page
const Expiring = Loadable(lazy(() => import('../pages/expiring')));

// render - group plans coverage page
const GroupPlansCoverage = Loadable(lazy(() => import('../pages/group-plans-coverage')));

// render - group plans excluded procedures page
const GroupPlansExcludedProcedures = Loadable(lazy(() => import('../pages/group-plans-excluded-procedures')));

// render - group plans benefit years page
const GroupPlansBenefitYears = Loadable(lazy(() => import('../pages/group-plans-benefit-years')));

// render - permissions page
const Permissions = Loadable(lazy(() => import('../pages/permissions')));

// render - test table page
const TestTable = Loadable(lazy(() => import('../pages/test-table')));

// render - reports pages
const Reports = Loadable(lazy(() => import('../pages/reports')));
const ReportsMembers = Loadable(lazy(() => import('../pages/reports-members')));
const ReportsInvoices = Loadable(lazy(() => import('../pages/reports-invoices')));
const ReportsClaims = Loadable(lazy(() => import('../pages/reports-claims')));
const CentresSplits = Loadable(lazy(() => import('../pages/centres-splits')));
const ReportsEligibility = Loadable(lazy(() => import('../pages/reports-eligibility')));
const ReportsBroker = Loadable(lazy(() => import('../pages/reports-broker')));
const ReportsDiscrepancies = Loadable(lazy(() => import('../pages/reports-discrepancies')));
const ReportsBrowser = Loadable(lazy(() => import('../pages/reports-browser')));

// render - audit page (root only)
const Audit = Loadable(lazy(() => import('../pages/audit')));
const EmailsSent = Loadable(lazy(() => import('../pages/emails-sent')));

// render - profile page
const Profile = Loadable(lazy(() => import('../pages/profile')));

// render - black box (paid claim adjudication stub; not in sidebar — navigate to /blackbox)
const Blackbox = Loadable(lazy(() => import('../pages/blackbox')));

// render - calendar playground (not in sidebar — navigate to /test/calendar)
const TestCalendar = Loadable(lazy(() => import('../pages/test-calendar')));

// not in sidebar — navigate to /system-info
const SystemInfo = Loadable(lazy(() => import('../pages/system-info')));

// ==============================|| MAIN ROUTING ||============================== //

const MainRoutes = {
  path: '/',
  element: <AuthGuard><DashboardLayout /></AuthGuard>,
  children: [
    {
      path: '',
      element: <Dashboard />
    },
    {
      path: 'users',
      element: <PermissionGuard module="users" minPermission={10}><Users /></PermissionGuard>
    },
    {
      path: 'clients',
      element: <PermissionGuard module="clients" minPermission={10}><Clients /></PermissionGuard>
    },
    {
      path: 'centres/splits',
      element: <PermissionGuard module="centres" minPermission={10}><CentresSplits /></PermissionGuard>
    },
    {
      path: 'centres',
      element: <PermissionGuard module="centres" minPermission={10}><Centres /></PermissionGuard>
    },
    {
      path: 'dentists',
      element: <PermissionGuard module="dentists" minPermission={10}><Dentists /></PermissionGuard>
    },
    {
      path: 'brokers',
      element: <PermissionGuard module="brokers" minPermission={10}><Brokers /></PermissionGuard>
    },
    {
      path: 'carriers',
      element: <PermissionGuard module="carriers" minPermission={10}><Carriers /></PermissionGuard>
    },
    {
      path: 'payments',
      element: <PermissionGuard module="payments" minPermission={10}><Payments /></PermissionGuard>
    },
    {
      path: 'claims/payment-schedule',
      element: <PermissionGuard module="claims" minPermission={10}><ClaimsPaymentSchedule /></PermissionGuard>
    },
    {
      path: 'claims',
      element: <PermissionGuard module="claims" minPermission={10}><Claims /></PermissionGuard>
    },
    {
      path: 'reports/members',
      element: <PermissionGuard module="reports" minPermission={10}><ReportsMembers /></PermissionGuard>
    },
    {
      path: 'reports/invoices',
      element: <PermissionGuard module="reports" minPermission={10}><ReportsInvoices /></PermissionGuard>
    },
    {
      path: 'reports/claims',
      element: <PermissionGuard module="reports" minPermission={10}><ReportsClaims /></PermissionGuard>
    },
    {
      path: 'reports/browser',
      element: <PermissionGuard module="reports" minPermission={10}><ReportsBrowser /></PermissionGuard>
    },
    {
      path: 'reports/eligibility',
      element: <PermissionGuard module="reports" minPermission={10}><ReportsEligibility /></PermissionGuard>
    },
    {
      path: 'reports/broker',
      element: <PermissionGuard module="reports" minPermission={10}><ReportsBroker /></PermissionGuard>
    },
    {
      path: 'reports/discrepancies',
      element: <DevUserGuard><ReportsDiscrepancies /></DevUserGuard>
    },
    {
      path: 'reports',
      element: <PermissionGuard module="reports" minPermission={10}><Reports /></PermissionGuard>
    },
    {
      path: 'specialists',
      element: <PermissionGuard module="specialists" minPermission={10}><Specialists /></PermissionGuard>
    },
    {
      path: 'emergency-dentists',
      element: <PermissionGuard module="emergency_dentists" minPermission={10}><EmergencyDentists /></PermissionGuard>
    },
    {
      path: 'divisions',
      element: <PermissionGuard module="clients" minPermission={10}><Divisions /></PermissionGuard>
    },
    {
      path: 'group-plans',
      element: <PermissionGuard module="group_plans" minPermission={10}><GroupPlans /></PermissionGuard>
    },
    {
      path: 'fee-guides',
      element: <PermissionGuard module="fee_guides" minPermission={10}><FeeGuides /></PermissionGuard>
    },
    {
      path: 'members/centre-transfer',
      element: <PermissionGuard module="members" minPermission={10}><CentreTransfer /></PermissionGuard>
    },
    {
      path: 'members',
      element: <PermissionGuard module="members" minPermission={10}><Members /></PermissionGuard>
    },
    {
      path: 'dependents/member/:memberId',
      element: <PermissionGuard module="members" minPermission={10}><Dependents /></PermissionGuard>
    },
    {
      path: 'dependents',
      element: <PermissionGuard module="members" minPermission={10}><Dependents /></PermissionGuard>
    },
    {
      path: 'group-plans/expiring',
      element: <PermissionGuard module="group_plans" minPermission={1}><Expiring /></PermissionGuard>
    },
    {
      path: 'group-plans/coverage/:id',
      element: <PermissionGuard module="group_plans" minPermission={10}><GroupPlansCoverage /></PermissionGuard>
    },
    {
      path: 'group-plans/excluded-procedures/:id',
      element: <PermissionGuard module="group_plans" minPermission={10}><GroupPlansExcludedProcedures /></PermissionGuard>
    },
    {
      path: 'group-plans/benefit-years/:id',
      element: <PermissionGuard module="group_plans" minPermission={10}><GroupPlansBenefitYears /></PermissionGuard>
    },
    {
      path: 'users/permissions/:userId',
      element: <PermissionGuard module="users" minPermission={30}><PermissionGuard module="permissions" minPermission={30}><Permissions /></PermissionGuard></PermissionGuard>
    },
    {
      path: 'test-table',
      element: <PermissionGuard module="test_table" minPermission={10}><TestTable /></PermissionGuard>
    },
    {
      path: 'audit',
      element: <RootGuard><Audit /></RootGuard>
    },
    {
      path: 'emails-sent',
      element: <DevUserGuard><EmailsSent /></DevUserGuard>
    },
    {
      path: 'profile',
      element: <Profile />
    },
    {
      path: 'blackbox',
      element: <Blackbox />
    },
    {
      path: 'test/calendar',
      element: <TestCalendar />
    },
    {
      path: 'system-info',
      element: <SystemInfo />
    },
    {
      path: '*',
      element: <NotFound />
    }
  ]
};

export default MainRoutes;
